import moment from "moment-timezone";
import { array, boolean, InferType, number, object, string } from "yup";
import { TLangType } from "./LangType";
import { PBNSchema } from "./PBN";
import { RegistrarAccountSchema } from "./RegistrarAccount";
import { ThematicSchema } from "./Thematic";
import { UserSchema } from "./UserModel";

export interface IRDByThematicIds {
    rdId: number;
    domain: string;
    thematic: string;
    thematicId: number;
}

export type TContent =
    | "netlinking"
    | "ranking"
    | "filling"
    | "discover"
    | "lead"
    | "affiliation"
    | "boost";

export const RDForT1Schema = object({
    rdId: number().required(),
    rdName: string().required(),
    pbnName: string().required(),
    thematicName: string().required(),
    thematicId: number().required(),
});

export const ContentSchema = object({
    filling: number().default(0).nullable(),
    ranking: number().default(0).nullable(),
    netlinking: number().default(0).nullable(),
    discover: number().default(0).nullable(),
    lead: number().default(0).nullable(),
    affiliation: number().default(0).nullable(),
    boost: number().default(0).nullable(),
});

export const pageSpeedSchema = object().shape({
    desktop: number().notRequired(),
    mobile: number().notRequired(),
});

export const UpsertContentSchema = object().shape({
    filling: number().default(0).nullable(),
    ranking: number().default(0).nullable(),
    netlinking: number().default(0).nullable(),
    discover: number().default(0).nullable(),
    lead: number().default(0).nullable(),
    affiliation: number().default(0).nullable(),
    boost: number().default(0).nullable(),
});

export const SiteSchema = object({
    id: number().required(),
    active: boolean().required("error.site.active.required"),
    domain: string().required("error.site.domain.required"),
    persona: string().notRequired(),
    uniqueVisitors: number().notRequired(),
    buyDomain: boolean().default(false),
    needCategories: boolean().notRequired().default(false),
    statusCakeId: string().notRequired(),
    priceBought: number().required().default(0),
    isChecked: boolean(),
    managers: array().of(UserSchema.required()).nullable(),
    price: number().nullable(),
    pageSpeed: pageSpeedSchema.notRequired(),
    linker: UserSchema.nullable().default(null),
    admin: UserSchema.nullable().default(null),
    numberOfPlannings: number().nullable().notRequired(),
    thematic: ThematicSchema.required("error.site.thematic.required"),
    categories: array().of(string()).nullable().default(null),
    registrarAccount: RegistrarAccountSchema.nullable().default(null),
    pbn: PBNSchema.required("error.site.pbn.required"),
    performanceScoreDesktop: number().notRequired(),
    urlReportDesktop: string().notRequired(),
    performanceScoreMobile: number().notRequired(),
    urlReportMobile: string().notRequired(),
    content: ContentSchema.default({
        filling: 0,
        ranking: 0,
        netlinking: 0,
        discover: 0,
        lead: 0,
        boost: 0,
        affiliation: 0,
    }).nullable(),
    lang: string<TLangType>().default("FR"),
    expirationDate: string()
        .optional()
        .transform((v1) => {
            if (!v1) return;
            return moment(v1).tz("Europe/Paris").format("YYYY-MM-DD");
        }),
    da: number().nullable(),
    tf: number().nullable(),
    cf: number().nullable(),
    po: number().nullable(),
    rd: number().nullable(),
    ttf: string().nullable(),
    mt: string().nullable(),
    kw: number().nullable(),
    googleResult: number().nullable(),
    traffic: number().nullable(),
    status: string().nullable(),
    indexed: number().nullable(),
});
export const UpsertSiteDTOSchema = SiteSchema.pick([
    "domain",
    "expirationDate",
    "categories",
    "buyDomain",
    "priceBought",
    "persona",
    "lang",
    "mt",
    "needCategories",
]).concat(
    object({
        managerIds: array().of(number().required()).nullable(),
        content: UpsertContentSchema,
        postByDefault: boolean().notRequired().default(false),
        linkerId: number().nullable(),
        adminId: number().nullable(),
        thematicId: number().required("error.site.thematicId.required"),
        buyDomain: boolean().required().default(true),
        managedByThirdParty: boolean().default(false),
        ipId: number().nullable(),
        registrarAccountId: number().when("managedByThirdParty", {
            is: (managedByThirdParty: boolean) => !managedByThirdParty,
            then: (schema) => schema.required("error.site.registrarAccountId.required"),
        }),
        pbnId: number().required("Pbn is required"),
    })
);

export const UpsertSiteAddDomainDTOSchema = UpsertSiteDTOSchema.pick([
    "domain",
    "thematicId",
    "pbnId",
    "needCategories",
]);

export const UpsertSiteAddUsersDTOSchema = UpsertSiteDTOSchema.pick([
    "adminId",
    "managerIds",
    "linkerId",
    "content",
]);
export const UpsertSiteAddCategoriesDTOSchema = object({
    mt: string().required(),
    categories: array().of(string()).nullable().default(null),
});

export const SiteInfraGridSchema = object({
    id: number().required(),
    domain: string().nonNullable(),
    pbn: string().nonNullable(),
    thematic: string().nonNullable(),
    admin: string().nullable(),
    googleResult: number().nullable().default(0),
    expirationDate: string()
        .optional()
        .transform((v1) => {
            if (!v1) return;
            return moment(v1).tz("Europe/Paris").format("YYYY-MM-DD");
        }),
    registrar: string().nullable(),
    login: string().nullable(),
    password: string().nullable(),
    urlReportDesktop: string().nullable(),
    active: boolean().required("error.site.active.required"),
    performanceScoreDesktop: number().nullable().default(0),
    urlReportMobile: string().nullable(),
    performanceScoreMobile: number().nullable().default(0),
});

export const SiteSeoGridSchema = object({
    id: number().required(),
    lang: string<TLangType>().default("FR"),
    domain: string().nonNullable(),
    pbn: string().nonNullable(),
    thematic: string().nonNullable(),
    content: ContentSchema.nullable(),
    admin: string().nullable(),
    managers: array()
        .of(string().nonNullable())
        .transform((v1: string | null) => {
            if (!v1) return;
            return v1.split(",");
        }),
    linker: string().nullable(),
    categories: array().of(string().nonNullable()).nonNullable(),
    googleResult: number().nullable().default(0),
    statusCakeId: string().nullable(),
    da: number().nullable().default(0),
    tf: number().nullable().default(0),
    cf: number().nullable().default(0),
    ttf: string().nullable(),
    rd: number().nullable().default(0),
    traffic: number().nullable().default(0),
    kw: number().nullable().default(0),
    indexed: number().nullable().default(0),
    numberOfPlannings: number().nullable().default(0),
    po: number().nullable().default(0),
    performanceScoreDesktop: number().nullable().default(0),
    urlReportDesktop: string().nullable(),
    performanceScoreMobile: number().nullable().default(0),
    urlReportMobile: string().nullable(),
    active: boolean().required("error.site.active.required"),
});

export const SiteTrafficGridSchema = object({
    id: number().required(),
    active: boolean().required(),
    domain: string().required(),
    pbn: string().required(),
    thematic: string().required(),
    avg1: number().nullable(),
    avg2: number().nullable(),
    avg3: number().nullable(),
    avg4: number().nullable(),
    avg5: number().nullable(),
    avg12: number().nullable(),
    thisMonth: number().nullable(),
    traffic: number().nullable(),
    po: number().nullable().default(0),
});

export type UpsertSiteDTO = InferType<typeof UpsertSiteDTOSchema>;
export type Content = InferType<typeof ContentSchema>;
export type Site = InferType<typeof SiteSchema>;
export type RDForT1 = InferType<typeof RDForT1Schema>;
export type SiteInfraGrid = InferType<typeof SiteInfraGridSchema>;
export type SiteSeoGrid = InferType<typeof SiteSeoGridSchema>;
export type SiteTrafficGrid = InferType<typeof SiteTrafficGridSchema>;
